'use client'

import PropTypes from 'prop-types'
import { TextEntry } from '@repo/ui/TextEntry'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { LargeUserIcon } from '@repo/ui/LargeUserIcon'
import { useState, useEffect, useCallback } from 'react'
import MUIIconButton from '@mui/material/IconButton'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import { CrossIcon } from '@repo/ui/CrossIcon'
import { LargeHouseIcon } from '@repo/ui/LargeHouseIcon'
import { LargeEmailIcon } from '@repo/ui/LargeEmailIcon'
import { LargeBillingAddressIcon } from '@repo/ui/LargeBillingAddressIcon'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import { AddressInput } from '@repo/ui/AddressInput'
import { useDebounce } from '@uidotdev/usehooks'
import { useTheme } from '@mui/material/styles'
import { useNewClientStore } from '@repo/store/useNewClientStore'

const sanitizeSingleLine = (str) => str.replace(/[\r\n]/g, '')

export const NewClientForm = ({ id, index, defaultValue = {}, onChange, onRemove, validationErrors = {} }) => {
  const theme = useTheme()

  const {
    billingHouse,
    billingArea,
    billingPostalTown,
    billingCounty,
    billingPostcode,
    billingAddressee,
    highRisk,
    updateState
  } = useNewClientStore()

  const [jsonString, setJsonString] = useState('')
  const debouncedJsonString = useDebounce(jsonString, 400)

  const [clientType, setClientType] = useState(defaultValue.clientType || 'I')
  const [surname, setSurname] = useState(defaultValue.surname || '')
  const [title, setTitle] = useState(defaultValue.title || '')
  const [forename, setForename] = useState(defaultValue.forename || '')
  const [initials, setInitials] = useState(
    defaultValue.initials ||
      (clientType === 'I' && defaultValue.forename && defaultValue.forename.charAt(0).toUpperCase()) ||
      ''
  )
  const [emailAddress, setEmailAddress] = useState(defaultValue.emailAddress || '')

  const [house, setHouse] = useState(defaultValue.house || '')
  const [area, setArea] = useState(defaultValue.area || '')
  const [postalTown, setPostalTown] = useState(defaultValue.postalTown || '')
  const [county, setCounty] = useState(defaultValue.county || '')
  const [postcode, setPostcode] = useState(defaultValue.postcode || '')

  // Default billingAddressSameAsMainAddress to true
  const [billingAddressSameAsMainAddress, setBillingAddressSameAsMainAddress] = useState(true)

  // Whenever any of these fields change, we rebuild the JSON payload for onChange
  useEffect(() => {
    if (!onChange) return

    setJsonString(
      JSON.stringify({
        id,
        index,
        clientType,
        title,
        forename,
        initials,
        surname,
        emailAddress,
        house,
        area,
        postalTown,
        county,
        postcode
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientType, title, forename, initials, surname, emailAddress, house, area, postalTown, county, postcode])

  // Helper to push a billing‐address object into the store
  const pushBillingIntoStore = useCallback(
    ({ house: h, area: a, postalTown: pT, county: c, postcode: pC }) => {
      if (index !== 0) return
      updateState({ key: 'billingHouse', value: h })
      updateState({ key: 'billingArea', value: a })
      updateState({ key: 'billingPostalTown', value: pT })
      updateState({ key: 'billingCounty', value: c })
      updateState({ key: 'billingPostcode', value: pC })
    },
    [index, updateState]
  )

  // Whenever billingAddressSameAsMainAddress turns true, or main address changes while it's true, sync
  useEffect(() => {
    if (!billingAddressSameAsMainAddress) return
    pushBillingIntoStore({ house, area, postalTown, county, postcode })
  }, [billingAddressSameAsMainAddress, house, area, postalTown, county, postcode, pushBillingIntoStore])

  // Build up the JSON‐change callback whenever debouncedJsonString updates
  useEffect(() => {
    if (!onChange || !debouncedJsonString) return
    onChange(JSON.parse(debouncedJsonString))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedJsonString])

  const getClientName = () => {
    const clientNameParts = [forename, surname].filter(Boolean)
    if (clientNameParts.length) return clientNameParts.join(' ')
    return 'New Client'
  }

  // Called when the main address inputs change:
  const setAddress = ({ house: h, area: a, postalTown: pT, county: c, postcode: pC }) => {
    const sanitizedHouse = sanitizeSingleLine(h)
    const sanitizedArea = sanitizeSingleLine(a)
    const sanitizedPostalTown = sanitizeSingleLine(pT)
    const sanitizedCounty = sanitizeSingleLine(c)
    const sanitizedPostcode = sanitizeSingleLine(pC)

    setHouse(sanitizedHouse)
    setArea(sanitizedArea)
    setPostalTown(sanitizedPostalTown)
    setCounty(sanitizedCounty)
    setPostcode(sanitizedPostcode)

    // If billing==same, immediately push these to billing as well
    if (billingAddressSameAsMainAddress) {
      pushBillingIntoStore({
        house: sanitizedHouse,
        area: sanitizedArea,
        postalTown: sanitizedPostalTown,
        county: sanitizedCounty,
        postcode: sanitizedPostcode
      })
    }
  }

  const setBillingAddressee = (value) => {
    // only do this for the first named client
    if (index !== 0) return
    updateState({ key: 'billingAddressee', value: sanitizeSingleLine(value) })
  }

  // Handler for toggling the "same as main address" checkbox:
  const handleBillingCheckboxChange = (e) => {
    const checked = e.target.checked
    setBillingAddressSameAsMainAddress(checked)

    if (checked) {
      // Copy current main address into billing immediately
      pushBillingIntoStore({ house, area, postalTown, county, postcode })
    } else {
      // Clear out billing fields so the inputs start blank
      updateState({ key: 'billingHouse', value: '' })
      updateState({ key: 'billingArea', value: '' })
      updateState({ key: 'billingPostalTown', value: '' })
      updateState({ key: 'billingCounty', value: '' })
      updateState({ key: 'billingPostcode', value: '' })
    }
  }

  return (
    <div data-testid="NewClientForm">
      <WizardPanel>
        <WizardIconTitle title={getClientName()} icon={LargeUserIcon} sx={{ mb: 8, width: '80%' }} />

        {index !== 0 ? (
          <MUIIconButton
            sx={{
              position: 'absolute',
              zIndex: 1,
              top: '18px',
              right: '24px',
              width: '52px',
              height: '52px'
            }}
            onClick={() => onRemove(index)}
            title="Remove client"
          >
            <CrossIcon
              sx={{
                width: '24px',
                height: '24px',
                opacity: 0.75,
                color: theme?.palette?.secondary?.dark || 'black'
              }}
            />
          </MUIIconButton>
        ) : null}

        <WizardFormSection>
          <MUIRadioGroup
            value={clientType}
            name="clientType"
            row
            sx={{ width: '100%' }}
            onChange={(e) => setClientType(e.target.value)}
          >
            <MUIFormControlLabel value="I" control={<MUIRadio />} label="Individual" />
            <MUIFormControlLabel value="C" control={<MUIRadio />} label="Company" />
          </MUIRadioGroup>

          {clientType === 'I' ? (
            <TextEntry
              label="Title"
              name={`namedClients[${index}].title`}
              value={title}
              onChange={(v) => setTitle(sanitizeSingleLine(v))}
              size="medium"
              sx={{ flex: 1, minWidth: '340px', maxWidth: '478px' }}
              limit={10}
            />
          ) : null}

          {clientType === 'I' ? (
            <TextEntry
              label="First and middle initials"
              name={`namedClients[${index}].initials`}
              value={initials}
              onChange={(v) => setInitials(sanitizeSingleLine(v))}
              size="medium"
              sx={{ flex: 1, minWidth: '340px', maxWidth: '478px' }}
              limit={5}
            />
          ) : null}

          {clientType === 'I' ? (
            <TextEntry
              label="Forename"
              name={`namedClients[${index}].forename`}
              value={forename}
              onChange={(v) => setForename(sanitizeSingleLine(v))}
              size="medium"
              sx={{ flex: 1, minWidth: '340px', maxWidth: '478px' }}
              limit={30}
            />
          ) : null}

          <TextEntry
            label={clientType === 'I' ? 'Surname' : 'Company name'}
            name={`namedClients[${index}].surname`}
            value={surname}
            onChange={(v) => setSurname(sanitizeSingleLine(v))}
            size="medium"
            sx={{ flex: 1, minWidth: '340px', maxWidth: '478px' }}
            required
            limit={255}
          />

          {index === 0 ? (
            <MUIFormControlLabel
              sx={{ ml: -2, mr: 0, mt: 0, width: '100%' }}
              control={
                <MUICheckbox
                  checked={highRisk || highRisk === 'highRisk'}
                  onChange={(e) => updateState({ key: 'highRisk', value: e.target.checked })}
                />
              }
              label="This client is high risk"
            />
          ) : null}
        </WizardFormSection>

        <WizardFormSection sx={{ marginTop: '50px', width: '100%', maxWidth: '478px' }}>
          <WizardIconTitle title="Contact details" icon={LargeEmailIcon} sx={{ mb: 1 }} errors={validationErrors} />

          <TextEntry
            size="medium"
            name={`namedClients[${index}].emailAddress`}
            label="Email address"
            type="emailAddress"
            value={emailAddress}
            onChange={(v) => setEmailAddress(sanitizeSingleLine(v))}
            errors={validationErrors}
            limit={255}
          />
        </WizardFormSection>

        <WizardFormSection sx={{ marginTop: '50px' }}>
          <MUIStack direction="column" gap="16px" sx={{ width: '100%' }}>
            <WizardIconTitle title="Address details" icon={LargeHouseIcon} sx={{ mb: 1 }} />

            <AddressInput
              validationErrors={validationErrors}
              onChange={setAddress}
              defaultValue={{ house, area, postalTown, county, postcode }}
            />
          </MUIStack>
        </WizardFormSection>

        {index === 0 ? (
          <WizardFormSection sx={{ marginTop: '50px', marginBottom: '20px' }}>
            <MUIStack direction="column" gap="16px" sx={{ width: '100%' }}>
              <WizardIconTitle title="Billing details" icon={LargeBillingAddressIcon} sx={{ mb: 1 }} />

              <TextEntry
                size="medium"
                name="billingAddressee"
                label="Billing addressee"
                value={billingAddressee}
                onChange={(v) => setBillingAddressee(sanitizeSingleLine(v))}
                errors={validationErrors}
                limit={100}
                sx={{ maxWidth: '478px' }}
              />

              <MUIFormControlLabel
                sx={{ ml: -2, mr: 0, mt: -3, width: '100%' }}
                control={
                  <MUICheckbox checked={billingAddressSameAsMainAddress} onChange={handleBillingCheckboxChange} />
                }
                label="Billing address is same as above"
              />

              {!billingAddressSameAsMainAddress ? (
                <AddressInput
                  validationErrors={validationErrors}
                  onChange={({ house: h, area: a, postalTown: pT, county: c, postcode: pC }) => {
                    // When the user types into the “separate” billing inputs, push those into store
                    updateState({ key: 'billingHouse', value: sanitizeSingleLine(h) })
                    updateState({ key: 'billingArea', value: sanitizeSingleLine(a) })
                    updateState({
                      key: 'billingPostalTown',
                      value: sanitizeSingleLine(pT)
                    })
                    updateState({ key: 'billingCounty', value: sanitizeSingleLine(c) })
                    updateState({
                      key: 'billingPostcode',
                      value: sanitizeSingleLine(pC)
                    })
                  }}
                  defaultValue={{
                    house: billingHouse,
                    area: billingArea,
                    postalTown: billingPostalTown,
                    county: billingCounty,
                    postcode: billingPostcode
                  }}
                />
              ) : null}
            </MUIStack>
          </WizardFormSection>
        ) : null}
      </WizardPanel>
    </div>
  )
}

NewClientForm.propTypes = {
  id: PropTypes.any,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  defaultValue: PropTypes.object,
  index: PropTypes.number,
  validationErrors: PropTypes.object
}
